/**
 * The dashboard language, as a choice that belongs to the account.
 *
 * Two places let a person pick it: the Alpha welcome dialog on first sign in,
 * and the Account screen. Both go through useChooseLanguage, so a choice
 * switches the screen at once (prefs.lang, the same value t() reads) and is
 * saved on the account, where it follows the person to any browser they sign
 * in on. useAccountLanguageSync is the other half: when the account loads
 * with a saved language, the browser adopts it.
 *
 * The save is best effort. A failed save leaves the screen in the language
 * the person just picked; it only means a second browser will not know yet.
 */

import { useCallback, useEffect } from 'react';
import { saveAccountLanguage } from '../api';
import { captureEvent } from '../analytics/posthog';
import { useSessionStore } from '../auth/session';
import { useDemoStore } from '../mock/store';
import type { AccountLanguage } from '../types';

export function useChooseLanguage(): (next: AccountLanguage, source: string) => void {
  const setPref = useDemoStore((s) => s.setPref);

  return useCallback(
    (next, source) => {
      captureEvent('language_changed', { language: next, source });
      setPref('lang', next);

      const { account, setAccount } = useSessionStore.getState();
      if (!account) return;
      // Updated first so the sync below does not read the old saved value and
      // switch the screen back while the save is still in flight.
      setAccount({ ...account, language: next });
      saveAccountLanguage(next).catch((err: unknown) => {
        captureEvent('language_save_failed', {
          language: next,
          source,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    },
    [setPref],
  );
}

/** Mounted once, in App: apply the account's saved language when it loads. */
export function useAccountLanguageSync(): void {
  const saved = useSessionStore((s) => s.account?.language ?? null);
  const setPref = useDemoStore((s) => s.setPref);

  useEffect(() => {
    if (!saved) return;
    if (useDemoStore.getState().prefs.lang !== saved) setPref('lang', saved);
  }, [saved, setPref]);
}
